/**
 * Display formatting for the vendor dashboard's correction/discrepancy stats
 * (ENG-15588).
 *
 * Shared by the dashboard tiles (apps/oak/.../Manifests/VendorDashboard) and
 * the manifest-discrepancy export's "Vendor Stats" sheet
 * (apps/lambdas/export-data) — the sheet's promise is that it shows exactly
 * what the tiles show, so the value formatting has to live in one place or a
 * dashboard tweak silently desyncs the export.
 */

export const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Short label for a 1-12 month number; falls back to the number itself. */
export function monthLabel(month: number): string {
  return MONTH_LABELS[month - 1] ?? String(month);
}

/** Day counts render to one decimal, "—" when there's nothing to show. */
export function formatDays(days: number | null | undefined): string {
  if (days === null || days === undefined) return "—";
  return days.toFixed(1);
}

/**
 * Percentage of `part` in `total` to one decimal — "—" when the denominator
 * is zero (avoids 0/0 → NaN%). Used by the breakdown tables' "% Compliant" /
 * "% corrected" cells.
 */
export function formatPercent(part: number, total: number): string {
  if (total === 0) return "—";
  return `${((part / total) * 100).toFixed(1)}%`;
}

/**
 * Headline compliance %, calculated against "concluded" (on-time + late +
 * overdue, i.e. pending excluded) so a fresh backlog of pending requests
 * doesn't drag the number to 0%. Rounded to an integer — this is the donut's
 * center label. "—" when nothing has concluded yet.
 */
export function complianceHeadline(onTime: number, concluded: number): string {
  return concluded > 0 ? `${Math.round((onTime / concluded) * 100)}%` : "—";
}

/** Label for stats rows whose manifests didn't match a known vendor. */
export const NO_MATCHED_VENDOR_LABEL = "No matched vendor";

export function vendorDisplayLabel(vendor: string | null): string {
  return vendor ?? NO_MATCHED_VENDOR_LABEL;
}
